import {
    AREA_CODE_GENERIC_ERROR,
    AUTOCOMPLETE_EMPTY_ERROR,
    AUTOCOMPLETE_INVALID,
    CCNAME_EMPTY_ERROR,
    CCNAME_INVALID,
    CITY_EMPTY_ERROR,
    CITY_INVALID,
    COUNTRY_INVALID,
    EMAIL_EMPTY_ERROR,
    EMAIL_INVALID,
    EXPDATE_FORMAT_ERROR,
    EXPDATE_INVALID_DATE,
    EXPDATE_INVALID_FORMAT,
    FISTNAME_EMPTY_ERROR,
    FISTNAME_INVALID,
    LASTNAME_EMPTY_ERROR,
    LASTNAME_INVALID,
    MARKETSOURCE_INVALID,
    PASSWORD_EMPTY_ERROR,
    PASSWORD_INVALID,
    PHONENUMBER_EMPTY_ERROR,
    PHONENUMBER_INVALID,
    PIN_INVALID,
    PIN_LENGTH_ERROR,
    STATE_INVALID,
    STREET1_EMPTY_ERROR,
    STREET1_INVALID,
    USERNAME_EMPTY_ERROR,
    USERNAME_INVALID_CHARACTERS,
    USERNAME_LENGTH_ERROR,
    ZIPCODE_INVALID,
    ZIPCODE_LENGTH_ERROR,
    RENEWAL_DISCLAIMER_ERROR
} from '~/common/utils/errorMessaging';
import { action, computed, observable } from 'mobx';
import { isObject } from '~/common/utils/typeCheckers';
import { gtm, errorFormEvent } from '~/common/utils/tracking';
import { convertToPaidStore } from '../domain/convertToPaid';

class FormsClass {
    @observable FirstName = '';
    @observable FirstNameError = '';
    @observable LastName = '';
    @observable LastNameError = '';
    @observable Email = '';
    @observable EmailError = '';
    @observable UserKey = '';
    @observable Username = '';
    @observable UsernameError = '';
    @observable UsernameWarning = '';
    @observable Password = '';
    @observable PasswordError = '';
    @observable PasswordLogin = '';
    @observable PasswordLoginError = '';
    @observable ProductPhone = '';
    @observable ProductPhoneError = '';
    @observable ContactPhone = '';
    @observable ContactPhoneError = '';
    @observable Pin = '';
    @observable PinError = '';
    @observable Autocomplete = '';
    @observable AutocompleteError = '';
    @observable Street1 = '';
    @observable Street1Error = '';
    @observable City = '';
    @observable CityError = '';
    @observable StateCode = '';
    @observable StateCodeError = '';
    @observable ZipCode = '';
    @observable ZipCodeError = '';
    @observable CountryCode = '';
    @observable CountryCodeError = '';
    @observable IsNewsletter = true;
    @observable OptInSms = true;
    @observable OptInReadYourVoicemail = true;
    @observable MarketSourceComments = '';
    @observable MarketSourceCommentsError = '';
    @observable NameOnCard = '';
    @observable NameOnCardError = '';
    @observable ExpirationDate = '';
    @observable ExpirationDateError = '';
    @observable LocalAreaCode = '';
    @observable LocalAreaCodeError = '';
    @observable VanitySearchTerm = '';
    @observable VanitySearchTermError = '';
    @observable RubyPlan = '';
    @observable RubyPlanError = '';
    @observable CreditCardTypeInfo = {};
    @observable FormType = '';
    @observable IsFormInteracted = false;
    @observable AutoRenewal = false;
    @observable AutoRenewalError = '';
    @observable isCheckoutFormValid = false;
    @observable fieldsToCheck = { FirstName: 1, LastName: 1, Password: 1, Email: 1, Street1: 1, City: 1, StateCode: 1, ZipCode: 1, ContactPhone: 1, ExpirationDate: 1, card: 1, cvc: 1, NameOnCard: 1 };
    @observable trialConversionFieldsToCheck = { Street1: 1, City: 1, StateCode: 1, ZipCode: 1, ContactPhone: 1, ExpirationDate: 1, card: 1, cvc: 1, NameOnCard: 1 };
    @observable fieldLabelsCheckout = Object.keys(this.fieldsToCheck);
    @observable fieldLabelsTrialConversion = Object.keys(this.trialConversionFieldsToCheck);
    // setters
    // set all form values
    @action setFormData(input, val) {
        const { isUserConvertingToPaid } = convertToPaidStore;

        // if val is empty and field is not in fieldsToCheck, add it
        if (!val && !isUserConvertingToPaid && this.fieldLabelsCheckout.includes(input) && !this.fieldsToCheck[input]) {
            this.fieldsToCheck[input] = 1;
        }

        // if val is empty and field is not in trialConversionFieldsToCheck, add it
        if (!val && isUserConvertingToPaid && this.fieldLabelsTrialConversion.includes(input) && !this.trialConversionFieldsToCheck[input]) {
            this.trialConversionFieldsToCheck[input] = 1;
        }

        // If Email changes, reset the UserKey
        if (input === 'Email' && (val !== this[input] && this[input] !== '')) {
            this.setFormData('UserKey', '');
        }

        if (typeof input === 'string' && (typeof val === 'string' || typeof val === 'boolean')) {
            this[input] = val;
        }
    }

    // Set Form Type
    @action setFormType(type) {
        this.FormType = type;
    }

    // set special form value for creditcard
    @action setCreditCardTypeInfo(creditCardInfo) {
        this.CreditCardTypeInfo = isObject(creditCardInfo, this.CreditCardTypeInfo);
    }

    // newsletter
    @action toggleNewsletter() {
        this.IsNewsletter = !this.IsNewsletter;
    }

    // sms feature
    @action toggleOptInSms() {
        this.OptInSms = !this.OptInSms;
    }

    // read your voicemail feature
    @action toggleOptInReadYourVoicemail() {
        this.OptInReadYourVoicemail = !this.OptInReadYourVoicemail;
    }

    // set auto renewal
    @action toggleAutoRenewal() {
        this.AutoRenewal = !this.AutoRenewal;
        this.validateAutoRenewal();
    }

    // set auto renewal error
    @action validateAutoRenewal() {
        this.setCheckoutFormValid('', false, true);
        if (!this.AutoRenewal) {
            this.AutoRenewalError = RENEWAL_DISCLAIMER_ERROR;
        } else {
            this.AutoRenewalError = '';
        }
    }

    // remove error from input
    @action clearFieldError(name) {
        if (typeof name === 'string') {
            this.setCheckoutFormValid(name, false);
            this[`${name}Error`] = '';
        }
    }

    @action setFieldError(name, error) {
        if (typeof name === 'string' && typeof error === 'string') {
            this.setCheckoutFormValid(name, !!error);
            this[`${name}Error`] = error;
        }
    }

    // set field warning
    @action setFieldWarning(name, warning) {
        if (typeof name === 'string' && typeof warning === 'string') {
            this[`${name}Warning`] = warning;
        }
    }

    @action removeFieldWarning(name) {
        if (typeof name === 'string') {
            this[`${name}Warning`] = '';
        }
    }

    @action removeValidationErrors(fields) {
        fields.forEach((field) => {
            this.clearFieldError(field);
        });
    }

    @action validateForm(fields, callFormErrorEvent) {
        const errorMsgs = [];
        const erroredFields = [];
        this.removeValidationErrors(fields);
        fields.forEach((field) => {
            this[`${field}Validation`]();
            if (this[`${field}Error`]) {
                errorMsgs.push(this[`${field}Error`]);
                erroredFields.push(field);
            }
        });

        if (callFormErrorEvent && !!errorMsgs.length) {
            gtm(errorFormEvent(this.FormType, erroredFields.join(), errorMsgs.join()));
        }
    }

    @action doesFormHaveErrors(fields) {
        return fields.find((field) => {
            return this[`${field}Error`].length > 0;
        });
    }

    @action setCheckoutFieldsForTrialConversion() {
        const updatedFields = this.fieldsToCheck;
        delete updatedFields.Email;
        delete updatedFields.Password;
        delete updatedFields.FirstName;
        delete updatedFields.LastName;
        this.fieldsToCheck = updatedFields;
    }

    @action setCheckoutFormValid(field, hasError, renewal) {
        const { isUserConvertingToPaid } = convertToPaidStore;
        const fieldsToCheck = isUserConvertingToPaid ? this.trialConversionFieldsToCheck : this.fieldsToCheck;

        if (hasError) {
            fieldsToCheck[field] = 1;
            this.isCheckoutFormValid = false;
            return;
        } else if (field) {
            delete fieldsToCheck[field];
        }

        const fields = Object.keys(fieldsToCheck);
        if (fields.length === 0 && !renewal) {
            this.validateAutoRenewal();
            return;
        }

        if (fields.length === 0 && this.AutoRenewal) {
            this.isCheckoutFormValid = true;
        }
    }

    @action areFieldsEmpty(fields) {
        return !!fields.find((field) => {
            return this[`${field}`].length === 0;
        });
    }

    // Set boolean check if Form is interacted
    @action setFormInteracted(bool) {
        this.IsFormInteracted = bool;
    }

    @action isFormInteracted() {
        return this.IsFormInteracted;
    }

    // escapes characters in a string, so regExp works
    @action escapeRegExp(text) {
        return text ? text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&') : '';
    }

    // form validation

    @action isFieldEmpty(input) {
        return this[input] === '';
    }

    @action onlyNumbers(input, allowedChar = '') {
        const allowed = this.escapeRegExp(allowedChar);
        const regex = new RegExp(`[^0-9${allowed}]+`, 'g');
        if (input !== '') {
            return regex.test(this[input]);
        }
        return true;
    }

    @action onlyLetters(input, allowedChar = '') {
        const allowed = this.escapeRegExp(allowedChar);
        const regex = new RegExp(`[^a-zA-Z${allowed}]+`, 'g');
        if (input !== '') {
            return regex.test(this[input]);
        }
        return true;
    }

    @action onlyLatin(input, allowedChar = '') {
        const allowed = this.escapeRegExp(allowedChar);
        const regex = new RegExp(`[^A-zÀ-ÖØ-öø-ÿ${allowed}]+`, 'g');
        if (input !== '') {
            return regex.test(this[input]);
        }
        return true;
    }

    @action onlyNumbersAndLetters(input, allowedChar = '') {
        const allowed = this.escapeRegExp(allowedChar);
        const regex = new RegExp(`[^a-zA-Z0-9${allowed}]+`, 'g');
        if (input !== '') {
            return regex.test(this[input]);
        }
        return true;
    }

    @action hasInvalidCharacter(input, disallowedChar = '') {
        const disallowed = this.escapeRegExp(disallowedChar);
        const regex = new RegExp(`[${disallowed}]+`, 'g');
        if (input !== '') {
            return regex.test(this[input]);
        }
        return true;
    }

    @action isInputNotSelected(input) {
        return this[input] === '' || this[input] === false;
    }

    // GTM event for form validation errors
    @action validationErrorEvent = (error, field) => {
        if (error) {
            gtm(errorFormEvent(this.FormType, field, error));
        }
    };

    // error validation for specific fields

    @action FirstNameValidation() {
        let FirstNameError = '';
        if (this.isFieldEmpty('FirstName')) {
            FirstNameError = FISTNAME_EMPTY_ERROR;
        } else if (this.onlyNumbersAndLetters('FirstName', " -'")) {
            FirstNameError = FISTNAME_INVALID;
        }

        this.setCheckoutFormValid('FirstName', !!FirstNameError);
        this.FirstNameError = FirstNameError;
    }

    @action LastNameValidation() {
        let LastNameError = '';
        if (this.isFieldEmpty('LastName')) {
            LastNameError = LASTNAME_EMPTY_ERROR;
        } else if (this.onlyNumbersAndLetters('LastName', " -'")) {
            LastNameError = LASTNAME_INVALID;
        }
        this.setCheckoutFormValid('LastName', !!LastNameError);
        this.LastNameError = LastNameError;
    }

    @action NameOnCardValidation() {
        let NameOnCardError = '';
        if (this.isFieldEmpty('NameOnCard')) {
            NameOnCardError = CCNAME_EMPTY_ERROR;
        } else if (this.onlyNumbersAndLetters('NameOnCard', " -'")) {
            NameOnCardError = CCNAME_INVALID;
        }

        this.setCheckoutFormValid('NameOnCard', !!NameOnCardError);
        this.NameOnCardError = NameOnCardError;
    }

    @action EmailValidation() {
        let EmailError = '';
        if (this.isFieldEmpty('Email')) {
            EmailError = EMAIL_EMPTY_ERROR;
        } else if (!(this.Email.match(/^[A-zÀ-ÖØ-öø-ÿ0-9.!#$%&’*+/=?^-_`{|}~-]+@[A-zÀ-ÖØ-öø-ÿ0-9-_]+(\.[A-zÀ-ÖØ-öø-ÿ]+)+$/))) {
            EmailError = EMAIL_INVALID;
        }

        this.setCheckoutFormValid('Email', !!EmailError);
        this.EmailError = EmailError;
    }

    @action PasswordValidation() {
        let PasswordError = '';
        if (this.isFieldEmpty('Password')) {
            PasswordError = PASSWORD_EMPTY_ERROR;
            // min 8 characters, 1 lowercase, 1 uppercase, 1 number, 1 special character !@#$%^&*
        } else if (!this.Password.match(new RegExp('(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[-!@#\$%\^&\*])(?=.{8,29}$)'))) { // eslint-disable-line no-useless-escape
            PasswordError = PASSWORD_INVALID;
        }

        this.setCheckoutFormValid('Password', !!PasswordError);
        this.PasswordError = PasswordError;
    }

    @action PasswordLoginValidation() {
        let PasswordLoginError = '';
        if (this.isFieldEmpty('PasswordLogin')) {
            PasswordLoginError = PASSWORD_EMPTY_ERROR;
        }

        this.PasswordLoginError = PasswordLoginError;
    }

    @action ProductPhoneValidation() {
        let ProductPhoneError = '';
        if (this.parsedProductNumber.length < 10) {
            ProductPhoneError = PHONENUMBER_EMPTY_ERROR;
        } else if (this.onlyNumbers('ProductPhone', '()_- ')) {
            ProductPhoneError = PHONENUMBER_INVALID;
        }

        // this.setCheckoutFormValid('ProductPhone', !!ProductPhoneError);
        this.ProductPhoneError = ProductPhoneError;
    }

    @action ContactPhoneValidation() {
        let ContactPhoneError = '';
        if (this.parsedContactNumber.length < 10) {
            ContactPhoneError = PHONENUMBER_EMPTY_ERROR;
        } else if (this.onlyNumbers('ContactPhone', '()_- ')) {
            ContactPhoneError = PHONENUMBER_INVALID;
        }

        this.setCheckoutFormValid('ContactPhone', !!ContactPhoneError);
        this.ContactPhoneError = ContactPhoneError;
    }

    @action Street1Validation() {
        let Street1Error = '';
        if (this.isFieldEmpty('Street1')) {
            Street1Error = STREET1_EMPTY_ERROR;
        } else if (this.onlyNumbersAndLetters('Street1', ' #.()/-,')) {
            Street1Error = STREET1_INVALID;
        }

        this.setCheckoutFormValid('Street1', !!Street1Error);
        this.Street1Error = Street1Error;
    }

    @action AutocompleteValidation() {
        let AutocompleteError = '';
        if (this.isFieldEmpty('Autocomplete')) {
            AutocompleteError = AUTOCOMPLETE_EMPTY_ERROR;
        } else if (this.areFieldsEmpty(['Street1', 'City', 'ZipCode', 'StateCode', 'CountryCode'])) {
            AutocompleteError = AUTOCOMPLETE_INVALID;
        }

        this.setCheckoutFormValid('AutoComplete', !!AutocompleteError);
        this.AutocompleteError = AutocompleteError;
    }

    @action CityValidation() {
        let CityError = '';
        if (this.isFieldEmpty('City')) {
            CityError = CITY_EMPTY_ERROR;
        } else if (this.onlyLetters('City', ' #.')) {
            CityError = CITY_INVALID;
        }

        this.setCheckoutFormValid('City', !!CityError);
        this.CityError = CityError;
    }

    @action ZipCodeValidation() {
        let ZipCodeError = '';
        if (this.ZipCode.length < 3 || this.ZipCode.length > 12) {
            ZipCodeError = ZIPCODE_LENGTH_ERROR;
        } else if (this.onlyNumbersAndLetters('ZipCode', ' -')) {
            ZipCodeError = ZIPCODE_INVALID;
        }

        this.setCheckoutFormValid('ZipCode', !!ZipCodeError);
        this.ZipCodeError = ZipCodeError;
    }

    @action ExpirationDateValidation() {
        let ExpirationDateError = '';
        if (this.parsedExpDate.length !== 5) {
            ExpirationDateError = EXPDATE_FORMAT_ERROR;
        } else if (this.onlyNumbers('ExpirationDate', '/')) {
            ExpirationDateError = EXPDATE_INVALID_FORMAT;
        } else if (this.ExpirationMonth > 12) {
            ExpirationDateError = EXPDATE_INVALID_DATE;
        } else if (this.ExpirationYear < new Date().getFullYear()) {
            ExpirationDateError = EXPDATE_INVALID_DATE;
        }

        this.setCheckoutFormValid('ExpirationDate', !!ExpirationDateError);
        this.ExpirationDateError = ExpirationDateError;
    }

    @action PinValidation() {
        let PinError = '';
        if (this.Pin.length !== 4) {
            PinError = PIN_LENGTH_ERROR;
        } else if (this.onlyNumbers('Pin')) {
            PinError = PIN_INVALID;
        }

        this.PinError = PinError;
    }

    @action UsernameValidation() {
        let UsernameError = '';
        if (this.isFieldEmpty('Username')) {
            UsernameError = USERNAME_EMPTY_ERROR;
        } else if (this.Username.length < 3) {
            UsernameError = USERNAME_LENGTH_ERROR;
        } else if (this.onlyNumbersAndLetters('Username', "@.!#$%&'*+-/=?^_`{|}:~")) {
            UsernameError = USERNAME_INVALID_CHARACTERS;
        }

        this.UsernameError = UsernameError;
    }

    @action MarketSourceCommentsValidation() {
        let MarketSourceCommentsError = '';
        if (!this.isFieldEmpty('MarketSourceComments') && this.onlyNumbersAndLetters('MarketSourceComments', ' ')) {
            MarketSourceCommentsError = MARKETSOURCE_INVALID;
        }

        this.MarketSourceCommentsError = MarketSourceCommentsError;
    }

    @action StateCodeValidation() {
        let StateCodeError = '';
        if (this.isInputNotSelected('StateCode')) {
            StateCodeError = STATE_INVALID;
        }

        this.setCheckoutFormValid('StateCode', !!StateCodeError);
        this.StateCodeError = StateCodeError;
    }

    @action CountryCodeValidation() {
        let CountryCodeError = '';
        if (this.isInputNotSelected('CountryCode')) {
            CountryCodeError = COUNTRY_INVALID;
        }

        this.setCheckoutFormValid('CountryCode', !!CountryCodeError);
        this.CountryCodeError = CountryCodeError;
    }

    @action LocalAreaCodeValidation() {
        let LocalAreaCodeError = '';
        if (this.isFieldEmpty('LocalAreaCode') || this.LocalAreaCode.length < 3) {
            LocalAreaCodeError = AREA_CODE_GENERIC_ERROR;
        }

        this.LocalAreaCodeError = LocalAreaCodeError;
    }

    @action RubyPlanValidation() {
        let RubyPlanError = '';
        if (this.isInputNotSelected('RubyPlan')) {
            RubyPlanError = 'Please select a plan.';
        }

        this.RubyPlanError = RubyPlanError;
    }

    // computed

    @computed get parsedPin() {
        return Number(this.Pin);
    }

    @computed get parsedProductNumber() {
        return this.ProductPhone.replace(/\D/g, '');
    }

    @computed get parsedContactNumber() {
        return this.ContactPhone.replace(/\D/g, '');
    }

    @computed get parsedExpDate() {
        return this.ExpirationDate.replace('_', '');
    }

    @computed get ExpirationMonth() {
        // if valid expiration date
        if (this.ExpirationDate.length === 5 && (/(\d\d)\/(\d\d)/).test(this.ExpirationDate)) {
            // return 2 digit month
            return this.ExpirationDate.substring(0, 2);
        }
        return '';
    }

    @computed get ExpirationYear() {
        // if valid expiration date
        if (this.ExpirationDate.length === 5 && (/(\d\d)\/(\d\d)/).test(this.ExpirationDate)) {
            // return 4 digit year
            return `20${this.ExpirationDate.substring(3, 5)}`;
        }
        return '';
    }

    @computed get LowerCaseDomainEmail() {
        const emailArray = this.Email.split('@');
        if (!emailArray[0] || !emailArray[1]) return this.Email;

        return `${emailArray[0]}@${emailArray[1].toLowerCase()}`;
    }

    @computed get EmailDomail() {
        const emailArray = this.Email.split('@');
        return emailArray[1] || false;
    }

    @computed get DoesEmailHaveErrors() {
        return this.isFieldEmpty('Email') || !(this.Email.match(/^[A-zÀ-ÖØ-öø-ÿ0-9.!#$%&’*+/=?^-_`{|}~-]+@[A-zÀ-ÖØ-öø-ÿ0-9-_]+(\.[A-zÀ-ÖØ-öø-ÿ]+)+$/));
    }
}


const formsStore = new FormsClass();
export { formsStore, FormsClass };
